<!--
	@name app-client-dashboard-workflow-application-activity
	@description Client Application Workflow Activity
	@date 2024/02/06
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<v-sheet>
			<v-skeleton-loader v-if="loading" type="list-item-three-line" />

			<v-sheet v-else-if="!kfis?.length">
				<v-row>
					<v-col v-if="site && 'usesAirFlow' in site && site.usesAirFlow === false" cols="12">
						<p class="text-body-1 font-weight-bold mb-6">
							Increase efficiency by visually managing and tracking the latest status of current and any previously requested or recommended KFI's.
						</p>
						<ul class="text-body-1 pl-8 mb-8">
							<li class="mb-2">
								Directly navigate to your sourcing results and request your Key Facts Illustration.
							</li>
							<li class="mb-2">
								Efficiently track the status of your requested KFI from “Submitted,” through to “Completed” or “Failed”.
							</li>
							<li class="mb-0">
								Download a copy of the research results or KFI, as well as the ability to navigate directly to the lender's portal for application.
							</li>
						</ul>
						<v-tooltip :disabled="!sourcingAgeWarning" :open-on-focus="false" bottom max-width="360">
							<template #activator="{ on, attrs }">
								<span class="d-inline-block" v-bind="attrs" v-on="on">
									<v-btn color="primary" :disabled="isSourcingDisabled" @click="createSourcingSession()">
										Get started
									</v-btn>
								</span>
							</template>
							<span>{{ sourcingAgeWarning }}</span>
						</v-tooltip>
					</v-col>
					<v-col v-else cols="12">
						<video
							v-if="introResource"
							class="d-block ml-10 mb-6"
							:src="introResource.video"
							:poster="introResource.poster"
							controls
							width="50%"
							style="float: right"
						/>
						<p class="text-body-1 font-weight-bold mb-6">
							Increase efficiency by visually managing and tracking the latest status of current and any previously requested or recommended KFI's.
						</p>
						<ul class="text-body-1 pl-8 mb-6">
							<li class="mb-2">
								Directly navigate to your sourcing results and request your Key Facts Illustration.
							</li>
							<li class="mb-2">
								Efficiently track the status of your requested KFI from “Submitted,” through to “Completed” or “Failed”.
							</li>
							<li class="mb-0">
								Download a copy of the research results or KFI, as well as the ability to navigate directly to the lender's portal for application.
							</li>
						</ul>
						<p class="text-body-1 mb-8">
							Click on the intro video for an explanation on how you can navigate AirFlow's Key Facts Illustration tile and maximise its benefits and features.
						</p>
						<v-tooltip :disabled="!sourcingAgeWarning" :open-on-focus="false" bottom max-width="360">
							<template #activator="{ on, attrs }">
								<span class="d-inline-block" v-bind="attrs" v-on="on">
									<v-btn color="primary" :disabled="isSourcingDisabled" @click="createSourcingSession()">
										Get started
									</v-btn>
								</span>
							</template>
							<span>{{ sourcingAgeWarning }}</span>
						</v-tooltip>
						<a class="text-body-2 font-weight-medium d-block mt-4" @click="openChat"> Need more help? Chat with our team. </a>
					</v-col>
				</v-row>
			</v-sheet>

			<div v-else class="pl-4">
				<div v-if="pendingKfis.length || failedKfis.length" class="ma-n1 pb-6">
					<p class="text-body-1 ma-1 mb-n1 font-weight-medium">
						Recommendations
					</p>
					<v-chip-group v-model="recommendation">
						<v-chip v-if="pendingKfis.length" filter color="warning darken-1" outlined label class="ma-1" value="pending">
							<v-icon small class="mr-2">
								mdi-alert
							</v-icon>{{ pendingKfis.length }} abandoned KFI requests
						</v-chip>
						<v-chip v-if="failedKfis.length" filter color="error" outlined label class="ma-1" value="failed">
							<v-icon small class="mr-2">
								mdi-alert
							</v-icon>{{ failedKfis.length }} KFI requests failed to process
						</v-chip>
					</v-chip-group>
				</div>
				<v-text-field v-if="items.length > 3" v-model="search" label="Search: e.g. Provider or Product name" outlined dense />
				<div class="overflow-y-auto overflow-x-hidden" :class="{ 'height--max': activeItems.length > 3 }">
					<v-timeline align-top dense class="pa-0">
						<v-timeline-item
							v-for="(item, index) in activeItems"
							:key="index"
							:color="item.completed ? 'grey' : 'grey'"
							small
							class="py-6"
							:class="{ 'kfi--processing':isProcessing(item) && isApiProduct(item) }"
						>
							<v-row no-gutters>
								<v-col class="pr-8">
									<p class="text-h6 mt-n1 mb-1 line-clamp" style="-webkit-line-clamp: 1">
										<span class="font-weight-bold">
											{{ item.product.name | truncate(50) }}
										</span>
										<v-chip label x-small dark :color="getStatusColor(getStatus(item.status))" style="position: relative; left: 8px; top: -4px">
											{{ getStatus(item.status) }}
										</v-chip>
									</p>
									<p class="text-body-2 mb-3">
										{{ item.product.provider.name }}
									</p>

									<v-timeline align-top dense class="pt-1">
										<v-timeline-item color="white" small fill-dot class="pb-2 remove-dot">
											<template #icon>
												<v-icon color="success">
													mdi-check-bold
												</v-icon>
											</template>
											<v-row no-gutters style="position: relative; top: 2px">
												<v-col class="pa-0" cols="12" sm="2">
													<span class="text-caption plain mb-0 d-block"> Created: </span>
												</v-col>
												<v-col class="pa-0">
													<span class="text-caption plain mb-0 d-block">
														{{ item.created | moment('MMM Do YYYY, h:mma') }}
													</span>
												</v-col>
											</v-row>
										</v-timeline-item>
										<v-timeline-item color="white" small fill-dot class="pb-2 remove-dot">
											<template #icon>
												<v-icon v-if="item.kfiRequestSubmitted" color="success">
													mdi-check-bold
												</v-icon>
												<v-icon v-else color="error">
													mdi-close-thick
												</v-icon>
											</template>
											<v-row no-gutters style="position: relative; top: 2px">
												<v-col class="pa-0" cols="12" sm="2">
													<span class="text-caption plain mb-0 d-block"> KFI Requested: </span>
												</v-col>
												<v-col class="pa-0">
													<span class="text-caption plain mb-0 d-block">
														<template v-if="item.kfiRequestSubmitted">{{ item.kfiRequestSubmitted | moment('MMM Do YYYY, h:mma') }}</template>
														<template v-else>--</template>
													</span>
												</v-col>
											</v-row>
										</v-timeline-item>
										<v-timeline-item v-if="isProcessing(item)" color="white" small fill-dot class="pb-2 remove-dot">
											<template #icon>
												<!-- Mirrors the KFI request page tile: spinning sync for API, account glyph for the
													 manually processed ones - see app-kfi-request-product -->
												<v-icon v-if="isApiProduct(item)" color="info" class="white icon--spin" style="border-radius: 100px">
													mdi-sync-circle
												</v-icon>
												<v-icon v-else color="secondary" class="white" style="border-radius: 100px">
													mdi-account-circle
												</v-icon>
											</template>
											<v-row no-gutters style="position: relative; top: 2px">
												<v-col class="pa-0" cols="12" sm="2">
													<span class="text-caption plain mb-0 d-block"> KFI Processing: </span>
												</v-col>
												<v-col class="pa-0">
													<span class="text-caption plain mb-0 d-block">
														<template v-if="isApiProduct(item)">Awaiting response from {{ item.product.provider.name }}…</template>
														<template v-else>Awaiting processing by our team</template>
													</span>
													<!-- <v-progress-linear indeterminate class="my-1" style="max-width: 300px" />
													<span class="text-caption plain grey--text text--darken-1 mb-0 d-block">
														The KFI will update as soon as the lender responds.
													</span> -->
												</v-col>
											</v-row>
										</v-timeline-item>
										<v-timeline-item
											v-else-if="item.kfiRequestSubmitted && !item.kfiRecieved && item.kfiRequestFailed"
											color="white"
											small
											fill-dot
											class="pb-2 remove-dot"
										>
											<template #icon>
												<v-icon color="error">
													mdi-alert
												</v-icon>
											</template>
											<v-row no-gutters style="position: relative; top: 2px">
												<v-col class="pa-0" cols="12" sm="2">
													<span class="text-caption plain mb-0 d-block"> KFI Request Failed: </span>
												</v-col>
												<v-col class="pa-0">
													<span class="text-caption plain mb-0 d-block">
														<template v-if="item.kfiRequestFailed">{{ item.kfiRequestFailed | moment('MMM Do YYYY, h:mma') }}</template>
														<template v-else>--</template>
													</span>
												</v-col>
											</v-row>
										</v-timeline-item>
										<v-timeline-item v-else color="white" small fill-dot class="pb-2 remove-dot">
											<template #icon>
												<v-icon v-if="item.kfiRecieved" color="success">
													mdi-check-bold
												</v-icon>
												<v-icon v-else color="error">
													mdi-close-thick
												</v-icon>
											</template>
											<v-row no-gutters style="position: relative; top: 2px">
												<v-col class="pa-0" cols="12" sm="2">
													<span class="text-caption plain mb-0 d-block"> KFI Received: </span>
												</v-col>
												<v-col class="pa-0">
													<span class="text-caption plain mb-0 d-block">
														<template v-if="item.kfiRecieved">{{ item.kfiRecieved | moment('MMM Do YYYY, h:mma') }}</template>
														<template v-else>--</template>
													</span>
												</v-col>
											</v-row>
										</v-timeline-item>
									</v-timeline>

									<div class="mt-4 mx-n1">
										<template v-for="(action, index) in getActions(item)">
											<v-tooltip :key="index" bottom content-class="text-center" max-width="300">
												<template #activator="{ on }">
													<div class="d-inline-block" v-on="on">
														<v-btn
															v-show="!action.hide"
															class="mx-1"
															:color="action.colour"
															:dark="'dark' in action ? action.dark : false"
															:disabled="action.disabled"
															:icon="'type' in action && action.type === 'icon'"
															:loading="fileLoading[item.id]"
															:small="'small' in action ? action.small : true"
															@click="handleAction(action, item)"
														>
															<v-icon v-if="action.icon" :color="'type' in action && action.type === 'icon' ? action.colour : 'white'">
																{{ action.icon }}
															</v-icon>
															<template v-else>
																{{ action.text }}
															</template>
														</v-btn>
													</div>
												</template>
												<span>{{ action.tooltip }}</span>
											</v-tooltip>
										</template>
									</div>
								</v-col>
								<v-col class="shrink pr-8 d-none">
									<common-base-image-tooltip
										:src="imageUrl(item) | urlize"
										:tooltip="item.product.provider.name"
										width="auto"
										height="auto"
										:max-width="150"
										:max-height="100"
										center
									/>
								</v-col>
							</v-row>
						</v-timeline-item>
					</v-timeline>
				</div>
			</div>
		</v-sheet>

		<app-client-property-dialog-apply ref="applicationDialog" @submit-application="submitApplication" />
		<common-dialog-confirm ref="confirm" />
		<app-kfi-request-failed-request-resubmit
			v-if="selectedProduct"
			ref="failedRequestDialog"
			:product="selectedProduct"
			:is-processing="isProcessing(selectedProduct)"
			:max-resubmit-attempts="maxResubmitAttempts"
			@update:product="updateProduct"
		/>
	</div>
</template>

<script>
	import { mapActions, mapGetters, mapState } from 'vuex';
	import { EventBus, ElementTools, PopupWindow } from '@/utils';
	import { isUnderSourcingAge, getSourcingAgeWarning } from '@/utils/sourcing-age';
	import documentMixin from '@/mixins/document';
	import CommonBaseImageTooltip from '@/component/common/base/image-tooltip';
	import CommonDialogConfirm from '@/component/common/dialog/confirm';
	import AppClientPropertyDialogApply from '../template/dialog/apply';
	import AppKfiRequestFailedRequestResubmit from '@/component/app/kfi-request/failed-request-resubmit';

	export default {
		name: 'app-client-dashboard-workflow-key-facts-illustration-activity',

		components: {
			CommonBaseImageTooltip,
			CommonDialogConfirm,
			AppClientPropertyDialogApply,
			AppKfiRequestFailedRequestResubmit
		},

		mixins: [documentMixin],

		props: {
			client: { type: Object, required: true },
			association: { type: Object, default: null },
			asset: { type: Object, required: true },
			introResource: { type: Object, required: false, default: null }
		},

		data() {
			return {
				loading: true,
				total: 0,
				search: null,
				kfis: null,
				fileLoading: {},
				recommendation: [],
				selectedProduct: null,
				maxResubmitAttempts: 3,
                socketStatus: null
			};
		},

		computed: {
			...mapState('CmsSite', ['site']),
			...mapGetters('AppWorkflow', ['getStatusColor']),

			items() {
				return this.kfis;
			},

			pendingKfis() {
				return this.items.filter((k) => k.status.toLowerCase().includes('pending'));
			},

			failedKfis() {
				return this.items.filter((k) => k.status.toLowerCase().includes('failed'));
			},

			activeItems() {
				let filtered = this.items;
				if (this.recommendation) {
					if (this.recommendation === 'pending') filtered = this.pendingKfis;
					if (this.recommendation === 'failed') filtered = this.failedKfis;
				}
				if (this.search) {
					filtered = filtered.filter((k) => {
						if (k.product.name.toLowerCase().includes(this.search.toLowerCase())) return true;
						if (k.product.provider.name?.toLowerCase().includes(this.search.toLowerCase())) return true;
						return false;
					});
				}
				return filtered;
			},

            socketsEnabled() {
				return this.socketStatus === 1;
			},

			isSourcingDisabled() {
				const clients = [this.client, this.association].filter(Boolean);
				return clients.length > 0 && clients.every((client) => isUnderSourcingAge(client));
			},

			sourcingAgeWarning() {
				return getSourcingAgeWarning(this.client, this.association);
			}
		},

		created() {
			this.loadKfis();
            this.socketStatus = this.$websocket.$status();
			this.$websocket.$on('message', this.onMessage);
            this.$websocket.$on('open', this.onWebSocketConnected);
		},

		beforeDestroy() {
			this.$websocket.$off('message', this.onMessage);
		},

		methods: {
			...mapActions('LifetimeMortgageKfi', ['fetchKfiAdvisorList', 'fetchFile', 'deleteProduct', 'sendKfiBackToQueue']),

			onMessage(message) {
				if (message?.type === 'factFind') {
					this.loadKfis();
				} else if (message?.type === 'kfi') {
					if (!message.action?.data?.status) return;

					// Status changes are pushed per user rather than per case, so messages land here for KFIs
					// belonging to other clients. Those aren't in this list - ignore them rather than writing
					// a junk index and announcing a product the adviser isn't looking at.
					const index = this.kfis?.findIndex((i) => i.id === message.id);
					if (!(index >= 0)) return;

					const status = message.action.data.status;
					const item = this.kfis[index];
					const data = { status };

					if (status === 'KFI Request Failed') data.kfiRequestFailed = message.action.created;
					this.$set(this.kfis, index, { ...item, ...data });

					this.notifyStatusChange(item, status);

					if (status === 'KFI Received') this.loadKfis();
				}
			},

			/**
			 * @name notifyStatusChange
			 * @description Announce every status change the socket reports, so the adviser is told what happened
			 * without having to read the timeline. The submitted case covers the adviser's own resubmit - the
			 * server echoes that status back, so it is not raised locally in updateProduct as well
			 * @param {Object} item The kfi submission the message relates to, as it was before the status change
			 * @param {String} status The new status
			 */
			notifyStatusChange(item, status) {
				const product = `"${item.product.name}" from ${item.product.provider.name}`;
				const options = { timeout: 6000, close: true };

				if (status === 'KFI Request Failed') ElementTools.fireNotification(this.$el, 'error', `The KFI request for ${product} has failed.`, options);
				else if (status === 'KFI Received')
					ElementTools.fireNotification(this.$el, 'success', `The KFI for ${product} has been received and is ready to download.`, options);
				else if (status === 'KFI Request Submitted') {
					this.fireKfiRequestSubmittedNotifcation(item);
				}
			},

            fireKfiRequestSubmittedNotifcation(item) {
                const product = `"${item.product.name}" from ${item.product.provider.name}`;
				const options = { timeout: 6000, close: true };

                // item still holds the pre-change status, so a prior failure means this was a resubmit
				const verb = item.status === 'KFI Request Failed' ? 'resubmitted' : 'submitted';
				ElementTools.fireNotification(this.$el, 'warning', `The KFI request for ${product} has been ${verb} and is awaiting processing.`, options);
            },

			submitApplication() {
				this.$emit('submit-application');
				this.$emit('workflow-updated');
				this.loadKfis();
			},

			loadKfis() {
				this.fetchKfiAdvisorList({
					entityIds: [this.asset.id],
					cumulative: true
				})
					.then(({ data }) => {
						this.total = data.total;
						this.kfis = data.data;
					})
					.catch(() => ElementTools.fireNotification(this.$el, 'error', `An error occured`))
					.finally(() => {
						this.loading = false;
						if (!this.kfis?.length) this.$emit('toggle-intro-resource');
					});
			},

			/**
			 * @name isApiProduct
			 * @description Products with an sqs queue go straight to the lender's API; the rest are worked by our
			 * team. Both are legitimately "in progress" - this only decides the wording and whether we spin
			 * @param {Object} item The kfi submission object
			 * @return {Boolean}
			 */
			isApiProduct(item) {
				return !!item?.product?.sqs;
			},

			/**
			 * @name isProcessing
			 * @description Is this KFI awaiting an outcome? Derived from the item rather than held as a session
			 * flag - the retry endpoint writes its own 'KFI Request Submitted' status action, so this stays true
			 * across a page reload. Deliberately not gated on sqs: resubmitting a manual product re-dates its
			 * status action, which lifts it back to the top of the team's KFI queue, so it is genuinely in progress
			 * @param {Object} item The kfi submission object
			 * @return {Boolean}
			 */
			isProcessing(item) {
				return item?.status === 'KFI Request Submitted' && !item.kfiRecieved;
			},

			getStatus(status) {
				if (status == 'KFI Request Submitted') return 'In Progress';
				if (status == 'KFI Request Failed') return 'Failed';
				if (status == 'KFI Received') return 'Completed';
				return 'Pending';
			},

			/**
			 * @name imageUrl
			 * @description Return image URL
			 * @return {String}
			 */
			imageUrl(item) {
				const primary = item?.product?.provider?.logo?.find((l) => l.primary);
				if (primary) return `logo/organisation/${primary.name}`;
				return null;
			},

			getActions(item) {
				let actions = [
					{
						title: 'Find out more',
						function: 'openFailedKfiRequest',
						functionParams: item,
						colour: 'primary',
						small: false,
						text: 'Find out more',
						tooltip: 'Instantly resubmit your KFI request with no additional data entry, or speak to our support team.',
						disabled: this.maxResubmitAttemptsReached(item.retryCount),
						hide: item.status != 'KFI Request Failed'
					},
					{
						title: 'Resume',
						path: `/request/${item?.matterId}?processTypeId=${item?.processTypeId}`,
						colour: 'primary',
						small: false,
						text: 'Resume',
						tooltip: 'This KFI request has not yet been submitted. Click here to continue.',
						disabled: !!item.kfiRequestSubmitted,
						hide: !!item.kfiRequestSubmitted
					},
					{
						title: 'Download KFI',
						function: `fetchDocument`,
						functionParams: item?.documents?.find((doc) => doc?.type === 'keyFactsIllustration'),
						small: false,
						text: 'Download KFI',
						colour: 'primary',
						tooltip: 'Download KFI',
						disabled: !item.kfiRecieved,
						hide: !item.kfiRequestSubmitted || item.status == 'KFI Request Failed'
					},
					{
						title: 'Apply',
						function: `proceedToApplication`,
						colour: 'secondary',
						small: false,
						text: 'Apply',
						tooltip: item.kfiRecieved
							? 'Apply for this product'
							: !item.kfiRequestSubmitted
							? 'Please submit the KFI request before attempting to apply for this product'
							: 'KFI not received',
						disabled: !item.kfiRecieved,
						hide: !item.kfiRequestSubmitted || item.productApplicationSubmitted || item.status == 'KFI Request Failed'
					},
					{
						title: 'Download Research PDF',
						function: 'fetchResearchPdf',
						functionParams: item?.documents?.find((doc) => doc?.type === 'researchPdf'),
						icon: 'mdi-file-table',
						colour: 'info',
						type: 'icon',
						tooltip: 'Download Research PDF'
					}
				];

				item?.documents
					?.filter((doc) => doc.type === 'supportingDocument')
					?.forEach((doc) => {
						actions.push({
							title: doc.title,
							function: 'fetchDocument',
							functionParams: doc,
							icon: 'mdi-file-document-plus',
							colour: 'info',
							type: 'icon',
							tooltip: doc.title || 'Supporting Document',
							disabled: !item.kfiRecieved,
							hide: !item.kfiRequestSubmitted
						});
					});

				actions.push({
					title: 'Delete',
					function: 'deleteProductSubmission',
					colour: 'error',
					icon: 'mdi-trash-can',
					type: 'icon',
					tooltip: 'Delete',
					hide: this.getStatus(item.status) !== 'Pending'
				});

				return actions;
			},

			/**
			 * @name handleAction
			 * @description execute correct action
			 * @param action item action
			 * @param item item that needs updating
			 */
			handleAction(action, item) {
				if (action.path) this.$router.push(action.path);
				if (action.function) this[action.function](item, action.functionParams);
			},

			/**
			 * @name proceedToApplication
			 * @description TODO
			 * @param action item action
			 */
			proceedToApplication(item) {
				this.$refs.applicationDialog.open(item);
			},

			apply(item) {
				this.$refs.applicationDialog.open(item);
			},

			openFailedKfiRequest(item) {
				this.selectedProduct = item;

				this.$nextTick(() => {
					this.$refs.failedRequestDialog.open();
				});
			},

			updateProduct(updatedProduct) {
				const index = this.kfis.findIndex((k) => k.id === updatedProduct.id);
				if (index !== -1) {
					// Stamp the resubmit time so the "KFI Requested" row no longer sits before the failure it
					// replaced - matches what the list endpoint returns on reload (newest status action wins)
					this.$set(this.kfis, index, { ...this.kfis[index], ...updatedProduct, kfiRequestSubmitted: new Date().toISOString() });
				}

                // No notification here unless sockets are disabled - the server echoes the 'KFI Request Submitted' status back over the
				// socket and notifyStatusChange raises it there, so raising it locally would double up
                if (!this.socketsEnabled) {
                    const item = this.kfis[index];
                    this.fireKfiRequestSubmittedNotifcation(item);
                }
			},

			/**
			 * @name fetchDocument
			 * @description TODO
			 * @param item
			 * @param data
			 */
			async fetchDocument(item, data) {
				this.$set(this.fileLoading, item.id, true);
				this.fetchFile({ id: data.id }).then(async(response) => {
					if (response.data?.preSignedUrl) new PopupWindow().open(response.data.preSignedUrl);
					this.$set(this.fileLoading, item.id, false);
				});
			},

			/**
			 * @name fetchResearchPdf
			 * @description TODO
			 * @param item
			 * @param data
			 */
			async fetchResearchPdf(item, data) {
				this.$set(this.fileLoading, item.id, true);
				const doc = {
					processId: data?.type === 'researchPdf' ? item.sourcing?.processId : item.processId,
					iterationId: item.sourcing?.iteration?.id,
					s3Key: data?.s3Key,
					filename: data?.filename
				};
				this.downloadDocument(doc).then(async() => {
					if (!data?.s3Key) await this.loadKfis();
					this.$set(this.fileLoading, item.id, false);
				});
			},

			/**
			 * @name deleteProductSubmission
			 * @description Delete product submission after confirmation
			 * @param {Object} item The kfi submission object
			 */
			deleteProductSubmission(item) {
				let msg = 'Are you sure you wish to delete this product submission?';
				this.$refs.confirm.open('Remove Product Submission', msg).then(async() => {
					this.$set(this.fileLoading, item.id, true);

					const payload = {
						processId: item.processId,
						iterationId: item.id,
						data: {
							action: {
								key: 'DeleteKeyFactsIllustrationProduct'
							}
						}
					};

					this.deleteProduct(payload)
						.then(() => {
							this.loadKfis();
							ElementTools.fireNotification(this.$el, 'success', `Product "${item.product.name} by ${item.product.provider.name}" has been successfully removed`);
						})
						.catch(() => ElementTools.fireNotification(this.$el, 'error', 'Failed to remove product submission'))
						.finally(() => this.$set(this.fileLoading, item.id, false));
				});
			},

			maxResubmitAttemptsReached(retryCount) {
				return retryCount >= this.maxResubmitAttempts;
			},

			/**
			 * @name openChat
			 */
			openChat() {
				EventBus.$emit('open-chat');
			},

			/**
			 * @name createSourcingSession
			 * @description Create New Sourcing Session
			 */
			createSourcingSession() {
				this.$router.push(
					`/source?clientId=${this.client?.id}${this.asset?.id ? `&propertyId=${this.asset.id}` : ''}${
						this.association?.id ? `&associationId=${this.association.id}` : ''
					}`
				);
			},

            onWebSocketConnected() {
				this.socketStatus = this.$websocket.$status();
			}
		}
	};
</script>

<style lang="scss" scoped>
	.remove-dot {
		::v-deep .v-timeline-item__dot--small {
			height: 0px !important;
			width: 0px !important;
			padding-top: 12px !important;

			.v-timeline-item__inner-dot {
				height: 0px !important;
				width: 0px !important;
				margin: 0 !important;
			}
		}
	}

	table {
		th,
		td {
			&.shrunk {
				width: 1px;
				white-space: nowrap;
			}

			&.nowrap {
				white-space: nowrap;
			}
		}
	}

	.v-timeline-item:has(.v-timeline-item):nth-child(even) {
		background: rgba(30, 176, 225, 0.1);
		background: linear-gradient(90deg, rgba(27, 120, 216, 0.05) 0%, rgba(103, 0, 86, 0.05) 100%);
	}

	.height--max {
		max-height: 32rem;
	}

    .kfi--processing {
		animation: kfi--processing 2s infinite;
	}

    @keyframes kfi--processing {
		0%,
		100% {
			border-color: $col5;
			background-color: tint($col1, 70%);
		}
		50% {
			border-color: $col4;
			background-color: tint($col3, 70%);
		}
	}
</style>
